/**
 * AdvancedDiagnostics.jsx — Shared collapsible wrapper for "for analysts" content.
 *
 * Use this anywhere raw technical metrics (P-max, entropy, fragility, sampled
 * distribution, probability spreads, mapping tables, etc.) need to be exposed
 * without competing with primary interpretation.
 *
 * Pattern:
 *   <AdvancedDiagnostics title="Sampled outcomes">
 *     <SampledChart ... />
 *   </AdvancedDiagnostics>
 *
 * - Closed by default
 * - Tagged "For analysts"
 * - Visually de-emphasized chrome (slate, muted)
 */
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import { cn } from '../../lib/utils';

export default function AdvancedDiagnostics({
  title = 'Advanced Diagnostics',
  description = null,
  badge = 'For analysts',
  defaultOpen = false,
  children,
  className,
  testId = 'advanced-diagnostics',
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card className={cn('border border-slate-200 dark:border-border', className)} data-testid={testId}>
        <CollapsibleTrigger asChild>
          <CardHeader className="pb-2 cursor-pointer hover:bg-slate-50/50 dark:hover:bg-white/[0.02] transition-colors">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <HelpCircle className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-300 truncate">
                  {title}
                </CardTitle>
                {badge && (
                  <Badge variant="outline" className="text-[10px] text-slate-400 flex-shrink-0">
                    {badge}
                  </Badge>
                )}
              </div>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0 flex-shrink-0" data-testid={`${testId}-toggle`}>
                {open ? (
                  <ChevronUp className="w-4 h-4 text-slate-400" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                )}
              </Button>
            </div>
            {description && (
              <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 leading-relaxed">{description}</p>
            )}
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0 space-y-4">{children}</CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}
